<script setup lang="ts">
import type { TreeNode } from 'primevue/treenode'
import { getAdminListIscoGroupsQueryKey, useAdminListIscoGroups, useAdminListIscoGroupJobs, useAdminSetIscoUnitGroupIdeal } from '@swipe4work/api-orval'
import type {
  AdminIscoGroupCrudIscoGroupDto,
  AdminIscoGroupCrudJobFunctionSummaryDto,
  AdminListJobsByFunctionJobDto,
  AdminListIscoGroupJobsParams,
} from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useRoute, useRouter } from 'vue-router'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useToast } from '@/shared/composables/UseToast'
import IscoCodePickerDialog from '../dialogs/IscoCodePickerDialog.vue'
import JobFunctionFormDialog from '../dialogs/JobFunctionFormDialog.vue'
import MergeJobFunctionDialog from '../dialogs/MergeJobFunctionDialog.vue'

const route = useRoute()
const router = useRouter()
const dialog = useDynamicDialog()
const toast = useToast()
const queryClient = useQueryClient()

definePage({
  path: '/data/isco-hierarchy',
  name: '/data/IscoHierarchy',
  meta: {
    title: 'ISCO-hiërarchie',
  },
})

// --- types ---
type IscoJobFunction = AdminIscoGroupCrudJobFunctionSummaryDto

interface IscoNodeData {
  code: string
  type: 'major' | 'submajor' | 'minor' | 'unit' | 'jobFunction' | 'unassigned'
  functionCount: number
  vacancyCount: number
  wzCount: number
  isIdeal: boolean
  idealCount: number
  jobFunction?: IscoJobFunction
}

interface IscoNode {
  key: string
  label: string
  data: IscoNodeData
  children: IscoNode[]
  leaf: boolean
}

// --- API data ---
const { data, isPending } = useAdminListIscoGroups({
  query: { staleTime: Infinity },
})

// --- helpers ---
function findNode(key: string, nodes: IscoNode[] = treeData.value): IscoNode | null {
  for (const node of nodes) {
    if (node.key === key) return node
    if (node.children.length) {
      const found = findNode(key, node.children)
      if (found) return found
    }
  }

  return null
}

function buildTreeFromApi(
  groups: AdminIscoGroupCrudIscoGroupDto[],
  jobFunctions: AdminIscoGroupCrudJobFunctionSummaryDto[],
): IscoNode[] {
  const byLevel: Record<number, AdminIscoGroupCrudIscoGroupDto[]> = {
    1: [],
    2: [],
    3: [],
    4: [],
  }

  for (const g of groups) {
    byLevel[g.level].push(g)
  }

  function labelForGroup(group: AdminIscoGroupCrudIscoGroupDto): string {
    return group.level === 1 ? `ISCO-1 ${group.preferredLabel}` : group.preferredLabel
  }

  function buildNode(group: AdminIscoGroupCrudIscoGroupDto): IscoNode {
    const childGroups = group.level < 4
      ? byLevel[group.level + 1].filter(g => g.parentCode === group.code)
      : []

    if (group.level === 4) {
      const jfs = jobFunctions.filter(jf => jf.isco4 === group.code)
      const children: IscoNode[] = jfs.map(jf => ({
        key: `jf-${jf.id}`,
        label: jf.name,
        data: {
          code: jf.isco4 ?? '',
          type: 'jobFunction',
          functionCount: 0,
          vacancyCount: jf.vacancyCount,
          wzCount: jf.wzCount,
          isIdeal: false,
          idealCount: 0,
          jobFunction: jf,
        },
        children: [],
        leaf: true,
      }))

      return {
        key: group.code,
        label: group.preferredLabel,
        data: {
          code: group.code,
          type: 'unit',
          functionCount: group.functionCount,
          vacancyCount: group.vacancyCount,
          wzCount: group.wzCount,
          isIdeal: group.isIdeal,
          idealCount: group.idealCount,
        },
        children,
        leaf: children.length === 0,
      }
    }

    const children = childGroups.map(buildNode)
    const types: Record<number, IscoNodeData['type']> = {
      1: 'major',
      2: 'submajor',
      3: 'minor',
    }

    return {
      key: group.code,
      label: labelForGroup(group),
      data: {
        code: group.code,
        type: types[group.level] ?? 'minor',
        functionCount: group.functionCount,
        vacancyCount: group.vacancyCount,
        wzCount: group.wzCount,
        isIdeal: group.isIdeal,
        idealCount: group.idealCount,
      },
      children,
      leaf: children.length === 0,
    }
  }

  return [ ...byLevel[1].map(buildNode), buildUnassignedNode(jobFunctions) ]
}

function buildUnassignedNode(jobFunctions: AdminIscoGroupCrudJobFunctionSummaryDto[]): IscoNode {
  const unassigned = jobFunctions.filter(jf => !jf.isco4)

  const children: IscoNode[] = unassigned.map(jf => ({
    key: `jf-${jf.id}`,
    label: jf.name,
    data: {
      code: '',
      type: 'jobFunction',
      functionCount: 0,
      vacancyCount: jf.vacancyCount,
      wzCount: jf.wzCount,
      isIdeal: false,
      idealCount: 0,
      jobFunction: jf,
    },
    children: [],
    leaf: true,
  }))

  return {
    key: 'unassigned',
    label: 'Niet-geclassificeerd',
    data: {
      code: '',
      type: 'unassigned',
      functionCount: unassigned.length,
      vacancyCount: unassigned.reduce((s, jf) => s + jf.vacancyCount, 0),
      wzCount: unassigned.reduce((s, jf) => s + jf.wzCount, 0),
      isIdeal: false,
      idealCount: 0,
    },
    children,
    leaf: children.length === 0,
  }
}

const treeData = ref<IscoNode[]>([])

function toTreeNodes(nodes: IscoNode[]): TreeNode[] {
  return nodes.map((n) => {
    const codeLabel = n.data.type === 'jobFunction' || n.data.type === 'unassigned' ? n.label : `${n.data.code}: ${n.label}`

    return {
      key: n.key,
      label: codeLabel,
      icon: iconForType(n.data.type),
      data: n.data,
      children: n.children.length > 0 ? toTreeNodes(n.children) : undefined,
      leaf: n.leaf,
      type: n.data.type,
    }
  })
}

function iconForType(type: string): string {
  switch (type) {
    case 'major': return 'fa-regular fa-layer-group'
    case 'submajor': return 'fa-regular fa-sitemap'
    case 'minor': return 'fa-regular fa-folder'
    case 'unit': return 'fa-regular fa-folder-open'
    case 'jobFunction': return 'fa-regular fa-briefcase'
    case 'unassigned': return 'fa-regular fa-circle-question'

    default: return 'fa-regular fa-file'
  }
}

const treeNodes = ref<TreeNode[]>([])
const expandedKeys = ref<Record<string, boolean>>({})
const allExpanded = ref(false)

watch(data, (newData) => {
  if (!newData) {
    treeData.value = []
    treeNodes.value = []
    expandedKeys.value = {}

    return
  }
  treeData.value = buildTreeFromApi(newData.iscoGroups, newData.jobFunctions)
  treeNodes.value = toTreeNodes(treeData.value)
  nextTick(() => {
    if (treeData.value.length > 0 && treeData.value[0].children.length > 0) {
      expandedKeys.value = collectExpandedKeys(treeNodes.value, 1)
    }
    // Restore selection from the URL (deep link / reload) once the tree exists.
    const sel = normalizeSel(route.query.sel)
    if (sel) selectByKey(sel)
  })
}, { immediate: true })

function statusSeverity(status: string): 'success' | 'warn' | 'secondary' {
  switch (status) {
    case 'Active': return 'success'
    case 'Draft': return 'warn'
    case 'Closed': return 'secondary'

    default: return 'secondary'
  }
}

const badgeLabel = (node: TreeNode): string => {
  const d = node.data
  const parts: string[] = []
  if (d.type !== 'jobFunction' && d.functionCount > 0) parts.push(`${d.functionCount} functies`)
  if (d.vacancyCount > 0) parts.push(`${d.vacancyCount} vacatures`)

  return parts.join(' | ')
}

// --- tree filtering ---
type FunctionFilter = 'all' | 'with' | 'without'
const functionFilter = ref<FunctionFilter>('all')
const functionFilterOptions: {
  label: string,
  value: FunctionFilter
}[] = [
  {
    label: 'Alle',
    value: 'all',
  },
  {
    label: 'Met functies',
    value: 'with',
  },
  {
    label: 'Zonder functies',
    value: 'without',
  },
]

// Ideal-only filter is persisted in the URL (`ideal=1`) so a filtered view can be shared.
const idealOnly = computed<boolean>({
  get: () => route.query.ideal === '1',
  set: (value) => {
    const rest = { ...route.query }

    delete rest.ideal

    router.replace({
      query: value
        ? {
          ...rest,
          ideal: '1', 
        }
        : rest, 
    })
  },
})

function filterIdeal(nodes: TreeNode[]): TreeNode[] {
  return nodes
    .filter(n => n.data.idealCount > 0)
    .map(n => n.data.type === 'unit'
      ? n
      : {
        ...n,
        children: filterIdeal(n.children ?? []),
      })
}

const filteredTreeNodes = computed(() => {
  const base = idealOnly.value ? filterIdeal(treeNodes.value) : treeNodes.value
  if (functionFilter.value === 'all') return base

  function filterWith(nodes: TreeNode[]): TreeNode[] {
    return nodes
      .map((n) => {
        if (n.data.type === 'jobFunction') return n
        const children = filterWith(n.children ?? [])
        if (children.length === 0) return null

        return {
          ...n,
          children,
        }
      })
      .filter(Boolean) as TreeNode[]
  }

  function filterWithout(nodes: TreeNode[]): TreeNode[] {
    return nodes
      .map((n) => {
        if (n.data.type === 'jobFunction') return null
        const children = filterWithout(n.children ?? [])
        if (n.data.functionCount === 0 || children.length > 0)
          return {
            ...n,
            children,
            leaf: children.length === 0,
          }

        return null
      })
      .filter(Boolean) as TreeNode[]
  }

  return functionFilter.value === 'with'
    ? filterWith(base)
    : filterWithout(base)
})

// --- state ---
const selectedKey = ref<string | null>(null)
const selectedType = ref<'function' | 'isco4' | 'isco3' | 'isco2' | 'isco1' | 'unassigned' | null>(null)
const selectedJobFunction = ref<IscoJobFunction | null>(null)
const selectedGroupFunctions = ref<{
  groupKey: string
  groupLabel: string
  functions: IscoJobFunction[]
}[]>([])
const iscoPath = ref<{
  code: string
  label: string
}[]>([])

// --- jobs by function ---
const jobsParamsRef = ref<AdminListIscoGroupJobsParams>({})
const jobsEnabled = computed(() => selectedType.value === 'function' && selectedJobFunction.value !== null)

watch(selectedJobFunction, (jf) => {
  jobsParamsRef.value = jf ? { jobFunctionId: jf.id } : {}
}, { immediate: true })

const { data: jobsData, isPending: isJobsLoading } = useAdminListIscoGroupJobs(jobsParamsRef, {
  query: { enabled: jobsEnabled },
})

const jobs = computed<AdminListJobsByFunctionJobDto[]>(() => jobsData.value?.jobs ?? [])

const selectedNode = computed<IscoNode | null>(() =>
  selectedKey.value ? findNode(selectedKey.value) : null,
)

function collectExpandedKeys(nodes: TreeNode[], maxLevel: number, level = 0): Record<string, boolean> {
  const keys: Record<string, boolean> = {}

  for (const n of nodes) {
    if (level < maxLevel && n.children?.length) {
      keys[n.key] = true
      Object.assign(keys, collectExpandedKeys(n.children, maxLevel, level + 1))
    }
  }

  return keys
}

function toggleExpandAll() {
  if (allExpanded.value) {
    expandedKeys.value = {}
    allExpanded.value = false
  }
  else {
    expandedKeys.value = collectExpandedKeys(treeNodes.value, Infinity)
    allExpanded.value = true
  }
}

/*
 * --- selection + URL routing ---
 * Selection state is derived from the `sel` query param so browser back/forward
 * and deep links work. Interaction handlers push to the router; a watcher applies
 * the selection whenever `sel` changes (click, back/forward, or initial load).
 */

function applySelection(node: IscoNode) {
  const d = node.data

  selectedKey.value = node.key
  iscoPath.value = buildPath(node.key)

  if (d.type === 'jobFunction' && d.jobFunction) {
    selectedType.value = 'function'
    selectedJobFunction.value = d.jobFunction
    selectedGroupFunctions.value = []
  } else if (d.type === 'unit') {
    selectedType.value = 'isco4'
    selectedJobFunction.value = null
    selectedGroupFunctions.value = [
      {
        groupKey: d.code,
        groupLabel: `${d.code}: ${iscoPath.value[iscoPath.value.length - 1]?.label ?? ''}`,
        functions: collectFunctions(node.key),
      },
    ]
  } else if (d.type === 'minor') {
    selectedType.value = 'isco3'
    selectedJobFunction.value = null
    selectedGroupFunctions.value = node.children
      .map((child) => ({
        groupKey: child.data.code,
        groupLabel: `${child.data.code}: ${child.label}`,
        functions: collectFunctions(child.key),
      }))
      .filter((g) => g.functions.length > 0)
  } else if (d.type === 'unassigned') {
    selectedType.value = 'unassigned'
    selectedJobFunction.value = null
    selectedGroupFunctions.value = [
      {
        groupKey: 'unassigned',
        groupLabel: 'Niet-geclassificeerd',
        functions: collectFunctions(node.key),
      },
    ]
  } else {
    selectedType.value = d.type === 'submajor' ? 'isco2' : 'isco1'
    selectedJobFunction.value = null
    selectedGroupFunctions.value = []
  }
}

function selectByKey(key: string) {
  const node = findNode(key)
  if (!node) return

  applySelection(node)

  // Expand ancestors so the selected node is revealed in the tree (deep links).
  const expanded = { ...expandedKeys.value }

  for (const level of buildPath(key)) {
    expanded[level.code] = true
  }
  expandedKeys.value = expanded
}

function clearSelection() {
  selectedKey.value = null
  selectedType.value = null
  selectedJobFunction.value = null
  selectedGroupFunctions.value = []
  iscoPath.value = []
}

function normalizeSel(value: unknown): string | null {
  const raw = Array.isArray(value) ? value[0] : value

  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

function navigate(key: string) {
  if (normalizeSel(route.query.sel) === key) {
    // Same key already in the URL (e.g. re-click) — ensure state is applied.
    selectByKey(key)

    return
  }
  router.push({
    query: {
      ...route.query,
      sel: key,
    },
  })
}

function onNodeSelect(node: TreeNode) {
  navigate(node.key)
}

watch(() => route.query.sel, (raw) => {
  const sel = normalizeSel(raw)
  if (!sel) {
    clearSelection()

    return
  }
  if (sel === selectedKey.value) return
  selectByKey(sel)
})

function buildPath(key: string): {
  code: string;
  label: string
}[] {
  const parts: {
    code: string;
    label: string
  }[] = []

  function walk(nodes: IscoNode[], target: string, ancestors: IscoNode[]): boolean {
    for (const n of nodes) {
      if (n.key === target) {
        for (const a of ancestors) parts.push({
          code: a.data.code,
          label: a.label,
        })
        parts.push({
          code: n.data.code,
          label: n.label,
        })

        return true
      }
      if (n.children.length && walk(n.children, target, [ ...ancestors, n ])) return true
    }

    return false
  }
  walk(treeData.value, key, [])

  return parts
}

function collectFunctions(key: string): IscoJobFunction[] {
  const node = findNode(key)
  if (!node) return []
  const result: IscoJobFunction[] = []

  function collect(n: IscoNode) {
    if (n.data.type === 'jobFunction' && n.data.jobFunction) result.push(n.data.jobFunction)
    for (const c of n.children) collect(c)
  }
  collect(node)

  return result
}

const setIdealMutation = useAdminSetIscoUnitGroupIdeal({
  mutation: {
    onSuccess: async (result) => {
      toast.success('Bijgewerkt', result.isIdeal ? `${result.code} is nu een Ideal Occupation.` : `${result.code} is geen Ideal Occupation meer.`)
      await queryClient.invalidateQueries({ queryKey: getAdminListIscoGroupsQueryKey() })
    },
    onError: () => toast.error('Fout', 'Er is iets misgegaan bij het bijwerken van de Ideal Occupation.'),
  },
})

function setIdeal(code: string, isIdeal: boolean) {
  setIdealMutation.mutate({
    code,
    data: { isIdeal },
  })
}

function selectJobFunction(jf: IscoJobFunction) {
  navigate(`jf-${jf.id}`)
}

function openIscoPicker(jf: IscoJobFunction) {
  dialog.open(IscoCodePickerDialog, {
    props: { header: 'ISCO-code toewijzen' },
    data: {
      entityType: 'jobFunction',
      entityId: jf.id,
      entityName: jf.name,
      currentIsco4: jf.isco4 ?? null,
    },
  })
}

function openCreateJobFunction(iscoUnitGroupCode?: string | null, iscoLabel?: string | null) {
  dialog.open(JobFunctionFormDialog, {
    props: { header: 'Nieuwe functie' },
    data: {
      mode: 'create',
      presetIscoUnitGroupCode: iscoUnitGroupCode ?? null,
      presetIscoLabel: iscoLabel ?? null,
    },
  })
}

function openRenameJobFunction(jf: IscoJobFunction) {
  dialog.open(JobFunctionFormDialog, {
    props: { header: 'Functie hernoemen' },
    data: {
      mode: 'rename',
      jobFunction: {
        id: jf.id,
        name: jf.name,
      },
    },
  })
}

function openMergeJobFunction(jf: IscoJobFunction) {
  dialog.open(MergeJobFunctionDialog, {
    props: { header: 'Functies samenvoegen' },
    data: {
      source: {
        id: jf.id,
        name: jf.name, 
      },
      initialTargetId: selectedJobFunction.value?.id ?? null,
    },
  })
}
</script>

<template>
  <div class="h-full flex flex-col gap-5 py-6">
    <div class="min-h-0 flex flex-1 gap-0 overflow-hidden border border-surface-200 rounded-lg">
      <!-- Tree pane -->

      <div class="min-w-[320px] w-[420px] overflow-y-auto border-r border-surface-200 bg-surface-50 p-3">
        <div class="mb-2 flex items-center justify-between px-2">
          <span class="text-sm text-surface-900 font-bold">ISCO-08 Hiërarchie</span>
          <Button
            :label="allExpanded ? 'Inklappen' : 'Uitklappen'"
            size="small"
            severity="secondary"
            text
            @click="toggleExpandAll"
          />
        </div>
        <div class="mb-2 px-2">
          <SelectButton v-model="functionFilter" :options="functionFilterOptions" option-label="label" option-value="value" size="small" class="w-full" />
          <label class="mt-2 flex cursor-pointer items-center gap-2 text-sm text-surface-700">
            <ToggleSwitch v-model="idealOnly" input-id="ideal-only" />
            Alleen Ideal Occupations
          </label>
        </div>
        <div v-if="isPending" class="px-2 space-y-2">
          <Skeleton v-for="n in 8" :key="n" height="3rem" />
        </div>
        <Tree
          v-else
          :value="filteredTreeNodes"
          v-model:expanded-keys="expandedKeys"
          selection-mode="single"
          :selection-keys="selectedKey ? { [selectedKey]: true } : {}"
          :filter="true"
          filter-mode="lenient"
          filter-placeholder="Zoek op ISCO-code of functie..."
          @node-select="onNodeSelect"
          class="w-full"
        >
          <template #node="slotProps">
            <div class="w-full flex items-center gap-2 py-0.5">
              <i :class="slotProps.node.icon" class="w-4 text-center text-surface-400" />
              <span class="flex-1 truncate text-sm">{{ slotProps.node.label }}</span>
              <i v-if="slotProps.node.data.isIdeal" v-tooltip.top="'Ideal Occupation'" class="fa-solid fa-star shrink-0 text-xs text-primary" />
              <span v-else-if="slotProps.node.data.idealCount > 0" class="shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary">
                {{ slotProps.node.data.idealCount }} ideal
              </span>
              <span v-if="badgeLabel(slotProps.node)" class="shrink-0 rounded-full bg-surface-200 px-2 py-0.5 text-xs text-surface-500">
                {{ badgeLabel(slotProps.node) }}
              </span>
            </div>
          </template>
        </Tree>
      </div>

      <!-- Detail pane -->
      <div class="flex-1 overflow-y-auto bg-white p-6">
        <!-- ISCO-3 grouped view -->
        <template v-if="selectedType === 'isco3'">
          <div class="mb-6">
            <h2>
              {{ iscoPath[2]?.label ?? 'ISCO-3 groep' }}
            </h2>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <Tag v-for="(level, i) in iscoPath" :key="i" :value="`${level.code}: ${level.label}`" :severity="i === iscoPath.length - 1 ? 'info' : 'secondary'" />
            </div>
          </div>
          <div v-if="selectedGroupFunctions.length > 0" class="space-y-6">
            <Card v-for="group in selectedGroupFunctions" :key="group.groupKey" class="overflow-hidden" :pt="{ body: { class: '!p-0' }, content: { class: '!p-0' } }">
              <template #content>
                <div class="border-b border-surface-200 bg-surface-50 px-4 py-2">
                  <p class="text-sm text-surface-900 font-bold">
                    {{ group.groupLabel }}
                  </p>
                  <span class="text-xs text-surface-500">
                    {{ group.functions.length }} functies
                    <span class="mx-1.5 text-surface-300">·</span>
                    {{ group.functions.reduce((s, jf) => s + jf.vacancyCount, 0) }} vacatures
                    <span class="mx-1.5 text-surface-300">·</span>
                    {{ group.functions.reduce((s, jf) => s + jf.wzCount, 0) }} werkzoekenden
                  </span>
                </div>
                <div class="divide-y divide-surface-100">
                  <div v-for="jf in group.functions" :key="jf.id" class="flex cursor-pointer items-center justify-between px-4 py-3 transition-colors hover:bg-surface-50" @click="selectJobFunction(jf)">
                    <div>
                      <div class="text-sm font-medium">
                        {{ jf.name }}
                      </div>
                      <div class="mt-0.5 flex gap-3 text-xs text-surface-400">
                        <span>{{ jf.vacancyCount }} vacatures</span>
                        <span>{{ jf.wzCount }} werkzoekenden</span>
                      </div>
                    </div>
                    <i class="fa-regular fa-chevron-right text-surface-300" />
                  </div>
                </div>
              </template>
            </Card>
          </div>
          <div v-else class="py-10 text-center text-surface-400">
            <i class="fa-regular fa-folder-open mb-3 block text-3xl" />
            <p class="text-sm">
              Geen functies in deze ISCO-3 groep
            </p>
          </div>
        </template>

        <!-- ISCO-4 flat list -->
        <template v-else-if="selectedType === 'isco4'">
          <div class="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2>
                {{ iscoPath[3]?.label ?? 'ISCO-4 groep' }}
              </h2>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <Tag v-for="(level, i) in iscoPath" :key="i" :value="`${level.code}: ${level.label}`" :severity="i === iscoPath.length - 1 ? 'info' : 'secondary'" />
              </div>
            </div>
            <Button
              label="Nieuwe functie"
              icon="fa-regular fa-plus"
              severity="secondary"
              outlined
              @click="openCreateJobFunction(iscoPath[3]?.code, iscoPath[3]?.label)"
            />
          </div>
          <Card v-if="selectedNode" class="mb-5">
            <template #content>
              <label class="flex cursor-pointer items-center justify-between gap-4">
                <span>
                  <span class="block text-sm text-surface-900 font-bold">Ideal Occupation (ICP)</span>
                  <span class="block text-sm text-surface-500">Gebruikers en vacatures met een functie in deze groep tellen als Ideal User / Ideal Job.</span>
                </span>
                <ToggleSwitch
                  :model-value="selectedNode.data.isIdeal"
                  :disabled="setIdealMutation.isPending.value"
                  @update:model-value="setIdeal(selectedNode.data.code, $event)"
                />
              </label>
            </template>
          </Card>
          <p class="mb-5 text-sm text-surface-600">
            <span class="text-surface-900 font-semibold">{{ selectedGroupFunctions[0]?.functions.length ?? 0 }}</span> functies
            <span class="mx-2 text-surface-300">·</span>
            <span class="text-surface-900 font-semibold">{{ selectedGroupFunctions[0]?.functions.reduce((s, jf) => s + jf.vacancyCount, 0) ?? 0 }}</span> vacatures
            <span class="mx-2 text-surface-300">·</span>
            <span class="text-surface-900 font-semibold">{{ selectedGroupFunctions[0]?.functions.reduce((s, jf) => s + jf.wzCount, 0) ?? 0 }}</span> werkzoekenden
          </p>
          <Card class="overflow-hidden" :pt="{ body: { class: '!p-0' }, content: { class: '!p-0' } }">
            <template #content>
              <div class="divide-y divide-surface-200">
                <div v-for="jf in (selectedGroupFunctions[0]?.functions ?? [])" :key="jf.id" class="flex cursor-pointer items-center justify-between px-4 py-3 transition-colors hover:bg-surface-50" @click="selectJobFunction(jf)">
                  <div>
                    <div class="text-sm font-medium">
                      {{ jf.name }}
                    </div>
                    <div class="mt-0.5 flex gap-3 text-xs text-surface-400">
                      <span>{{ jf.vacancyCount }} vacatures</span>
                      <span>{{ jf.wzCount }} werkzoekenden</span>
                    </div>
                  </div>
                  <i class="fa-regular fa-chevron-right text-surface-300" />
                </div>
                <div v-if="(selectedGroupFunctions[0]?.functions ?? []).length === 0" class="px-4 py-6 text-center text-sm text-surface-400">
                  Geen functies in deze ISCO-4 groep
                </div>
              </div>
            </template>
          </Card>
        </template>

        <!-- Unassigned: canonical Job Functions with no ISCO code yet -->
        <template v-else-if="selectedType === 'unassigned'">
          <div class="mb-5">
            <h2>Niet-geclassificeerd</h2>
            <p class="mt-1 text-sm text-surface-500">
              Functies zonder ISCO-code. Klik op een functie om er een toe te wijzen.
            </p>
          </div>
          <p class="mb-5 text-sm text-surface-600">
            <span class="text-surface-900 font-semibold">{{ selectedGroupFunctions[0]?.functions.length ?? 0 }}</span> functies
          </p>
          <Card class="overflow-hidden" :pt="{ body: { class: '!p-0' }, content: { class: '!p-0' } }">
            <template #content>
              <div class="divide-y divide-surface-200">
                <div v-for="jf in (selectedGroupFunctions[0]?.functions ?? [])" :key="jf.id" class="flex cursor-pointer items-center justify-between px-4 py-3 transition-colors hover:bg-surface-50" @click="selectJobFunction(jf)">
                  <div>
                    <div class="text-sm font-medium">
                      {{ jf.name }}
                    </div>
                    <div class="mt-0.5 flex gap-3 text-xs text-surface-400">
                      <span>{{ jf.vacancyCount }} vacatures</span>
                      <span>{{ jf.wzCount }} werkzoekenden</span>
                    </div>
                  </div>
                  <i class="fa-regular fa-chevron-right text-surface-300" />
                </div>
                <div v-if="(selectedGroupFunctions[0]?.functions ?? []).length === 0" class="px-4 py-6 text-center text-sm text-surface-400">
                  Geen niet-geclassificeerde functies
                </div>
              </div>
            </template>
          </Card>
        </template>

        <!-- ISCO-1 / ISCO-2 summary -->
        <template v-else-if="selectedType === 'isco1' || selectedType === 'isco2'">
          <div class="mb-5">
            <h2>
              {{ iscoPath[selectedType === 'isco1' ? 0 : 1]?.label ?? 'ISCO-groep' }}
            </h2>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <Tag v-for="(level, i) in iscoPath" :key="i" :value="`${level.code}: ${level.label}`" :severity="i === iscoPath.length - 1 ? 'info' : 'secondary'" />
            </div>
          </div>
          <p class="mb-5 text-sm text-surface-600">
            <span class="text-surface-900 font-semibold">{{ selectedNode?.data.functionCount ?? 0 }}</span> functies
            <span class="mx-2 text-surface-300">·</span>
            <span class="text-surface-900 font-semibold">{{ selectedNode?.data.vacancyCount ?? 0 }}</span> vacatures
            <span class="mx-2 text-surface-300">·</span>
            <span class="text-surface-900 font-semibold">{{ selectedNode?.data.wzCount ?? 0 }}</span> werkzoekenden
          </p>
          <Card class="overflow-hidden" :pt="{ body: { class: '!p-0' }, content: { class: '!p-0' } }">
            <template #content>
              <div class="divide-y divide-surface-100">
                <div v-for="child in (selectedNode?.children ?? [])" :key="child.key" class="flex items-center justify-between px-4 py-3">
                  <div>
                    <div class="text-sm text-surface-900 font-medium">
                      {{ child.data.code }}: {{ child.label }}
                    </div>
                    <div class="mt-0.5 flex gap-3 text-xs text-surface-400">
                      <span>{{ child.data.functionCount }} functies</span>
                      <span>{{ child.data.vacancyCount }} vacatures</span>
                      <span>{{ child.data.wzCount }} werkzoekenden</span>
                    </div>
                  </div>
                  <span class="shrink-0 text-xs text-surface-500">{{ selectedType === 'isco1' ? 'ISCO-2' : 'ISCO-3' }}</span>
                </div>
                <div v-if="!selectedNode?.children.length" class="px-4 py-6 text-center text-sm text-surface-400">
                  Geen subgroepen
                </div>
              </div>
            </template>
          </Card>
        </template>

        <!-- Single function detail -->
        <template v-else-if="selectedType === 'function' && selectedJobFunction">
          <div class="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2>
                {{ selectedJobFunction.name }}
              </h2>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <Tag v-if="!selectedJobFunction.isco4" value="Niet-geclassificeerd" severity="warn" />
                <Tag v-for="(level, i) in iscoPath" v-else :key="i" :value="`${level.code}: ${level.label}`" :severity="i === iscoPath.length - 1 ? 'info' : 'secondary'" />
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                label="Hernoemen"
                icon="fa-regular fa-pen"
                severity="secondary"
                outlined
                @click="openRenameJobFunction(selectedJobFunction)"
              />
              <Button
                label="ISCO-code toewijzen"
                icon="fa-regular fa-sitemap"
                severity="secondary"
                outlined
                @click="openIscoPicker(selectedJobFunction)"
              />
            </div>
          </div>
          <p class="mb-5 text-sm text-surface-600">
            <span class="text-surface-900 font-semibold">{{ selectedJobFunction.vacancyCount }}</span> vacatures
            <span class="mx-2 text-surface-300">·</span>
            <span class="text-surface-900 font-semibold">{{ selectedJobFunction.wzCount }}</span> werkzoekenden
          </p>
          <div class="mb-6">
            <p class="mb-3 text-sm text-surface-900 font-bold">
              Vacatures
            </p>
            <DataTable v-if="!isJobsLoading" :value="jobs" striped-rows class="text-sm">
              <Column field="title" header="Vacature">
                <template #body="{ data: job }">
                  <RouterLink :to="`/vacatures/${job.id}`" class="text-primary hover:underline">
                    {{ job.title }}
                  </RouterLink>
                </template>
              </Column>
              <Column field="companyName" header="Bedrijf">
                <template #body="{ data: job }">
                  <RouterLink :to="`/companies/${job.companyId}`" class="text-primary hover:underline">
                    {{ job.companyName }}
                  </RouterLink>
                </template>
              </Column>
              <Column field="status" header="Status">
                <template #body="{ data: job }">
                  <Tag :value="job.status" :severity="statusSeverity(job.status)" />
                </template>
              </Column>
              <DateColumn field="publishedAt" header="Gepubliceerd op" format="DD-MM-YY" />
            </DataTable>
            <div v-else class="space-y-2">
              <Skeleton v-for="n in 4" :key="n" height="3rem" />
            </div>
            <div v-if="!isJobsLoading && jobs.length === 0" class="py-4 text-center text-sm text-surface-400">
              Geen vacatures voor deze functie
            </div>
          </div>

          <div v-if="selectedJobFunction.isco4" class="space-y-4">
            <div v-if="iscoPath.length >= 4">
              <p class="mb-2 text-sm text-surface-900 font-bold">
                Andere functies in {{ iscoPath[3].code }}
              </p>
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="jf in collectFunctions(iscoPath[3].code).filter(j => j.id !== selectedJobFunction!.id)"
                  :key="jf.id"
                  class="flex items-center gap-1"
                >
                  <Button
                    @click="selectJobFunction(jf)"
                    outlined
                  >
                    {{ jf.name }}
                  </Button>
                  <Button
                    icon="fa-regular fa-code-merge"
                    severity="secondary"
                    text
                    v-tooltip.top="`Samenvoegen met ${selectedJobFunction!.name}`"
                    @click="openMergeJobFunction(jf)"
                  />
                </div>
              </div>
              <p v-if="collectFunctions(iscoPath[3].code).filter(j => j.id !== selectedJobFunction!.id).length === 0" class="text-sm text-surface-400">
                Geen andere functies in deze ISCO-4
              </p>
            </div>
            <Card class="overflow-hidden" :pt="{ body: { class: '!p-0' }, content: { class: '!p-0' } }">
              <template #content>
                <div class="divide-y divide-surface-100">
                  <div v-if="iscoPath.length >= 3" class="flex items-center justify-between px-4 py-2.5">
                    <span class="text-xs text-surface-400">ISCO-3 <span class="ml-1 text-sm text-surface-700 font-semibold">{{ iscoPath[2].code }}</span></span>
                    <div class="ml-4 flex shrink-0 gap-3 text-xs text-surface-400">
                      <span>{{ collectFunctions(iscoPath[2].code).length }} functies</span>
                      <span>{{ collectFunctions(iscoPath[2].code).reduce((s, jf) => s + jf.vacancyCount, 0) }} vacatures</span>
                      <span>{{ collectFunctions(iscoPath[2].code).reduce((s, jf) => s + jf.wzCount, 0) }} werkzoekenden</span>
                    </div>
                  </div>
                  <div v-if="iscoPath.length >= 2" class="flex items-center justify-between px-4 py-2.5">
                    <span class="text-xs text-surface-400">ISCO-2 <span class="ml-1 text-sm text-surface-700 font-semibold">{{ iscoPath[1].code }}</span></span>
                    <div class="ml-4 flex shrink-0 gap-3 text-xs text-surface-400">
                      <span>{{ collectFunctions(iscoPath[1].code).length }} functies</span>
                      <span>{{ collectFunctions(iscoPath[1].code).reduce((s, jf) => s + jf.vacancyCount, 0) }} vacatures</span>
                      <span>{{ collectFunctions(iscoPath[1].code).reduce((s, jf) => s + jf.wzCount, 0) }} werkzoekenden</span>
                    </div>
                  </div>
                  <div class="flex items-center justify-between px-4 py-2.5">
                    <span class="text-xs text-surface-400">ISCO-1 <span class="ml-1 text-sm text-surface-700 font-semibold">{{ iscoPath[0].code }}</span></span>
                    <div class="ml-4 flex shrink-0 gap-3 text-xs text-surface-400">
                      <span>{{ collectFunctions(iscoPath[0].code).length }} functies</span>
                      <span>{{ collectFunctions(iscoPath[0].code).reduce((s, jf) => s + jf.vacancyCount, 0) }} vacatures</span>
                      <span>{{ collectFunctions(iscoPath[0].code).reduce((s, jf) => s + jf.wzCount, 0) }} werkzoekenden</span>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </template>

        <!-- Empty state -->
        <template v-else>
          <div class="h-full flex items-center justify-center">
            <div class="text-center">
              <i class="fa-regular fa-sitemap mb-3 block text-4xl text-surface-300" />
              <p class="text-lg text-surface-700 font-medium">
                Selecteer een node in de boom
              </p>
              <p class="mt-1 text-sm text-surface-400">
                Klik op een ISCO-groep of functie om details te zien
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
